//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ReactNode } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { Box, Grid, GridSize, Palette } from '@mui/material';
import { convertStylesToStyled } from '@/_utils/utils';

interface ValueProps {
  label: string | ReactNode;
  children?: string | ReactNode;
}

interface LabelProps {
  horizontal?: boolean;
  color?: keyof Palette;
  size?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', GridSize>>;
  className?: string;
  labelOnly?: boolean;
  sx?: CSSObject;
}

const LabelValue = ({ horizontal, labelOnly, color = 'primary', size, className, children, sx, ...props }: ValueProps & LabelProps) => {
  return (
    <Grid item {...size} className={className} sx={sx}>
      <Wrapper horizontal={horizontal}>
        <Label color={color}>
          {props.label}
          {horizontal && ':'}
        </Label>
        {!labelOnly && <Value {...props}>{children}</Value>}
      </Wrapper>
    </Grid>
  );
};

export default LabelValue;

const Wrapper = styled.div<{ horizontal?: boolean }>`
  display: flex;
  flex-direction: ${({ horizontal }) => (horizontal ? 'row' : 'column')};
  column-gap: 7px;
`;

export const Label = styled.div<{ color?: string }>`
  opacity: 0.7;
  font-size: 0.86rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightNormal};
  margin-bottom: 4px;
  cursor: default;
  color: ${({ color, theme }) => {
    let labelColor = color ? theme.palette[color as keyof Palette].main : theme.palette.text.primary;
    if (color === 'primary') {
      const colorMode = theme.palette.mode === 'light' ? 'dark' : 'light';
      labelColor = theme.palette.primary[colorMode];
    }
    return labelColor;
  }};
`;

const Value = (props: ValueProps) => {
  if (props.children === true) {
    // i'm not sure what this is for but removing it breaks completely unrelated things somehow
    return <Children>true</Children>;
  } else {
    return <Children>{props.children || '-'}</Children>;
  }
};

export const Children = styled(Box)<{ styles?: CSSObject }>`
  min-height: 20px;
  word-break: break-word;
  line-height: 1.2;
  ${({ styles }) => convertStylesToStyled(styles)}
`;
